/**
 * TenantAccessDeniedScreen — shown when the active tenant in the URL is one the
 * signed-in account holds no membership in (server: 403 `tenant_access_denied`).
 *
 * Why a full screen and not a dialog (#341)
 * -----------------------------------------
 * A dialog sits over a working app. Nothing is working here: the tenant id comes
 * from the URL and is attached to EVERY tenant-scoped request, so all thirteen
 * `resolveTenant` prefixes reject at once — files, workspaces, bin, agent,
 * notifications, the lot. Rendering the shell behind a modal would show empty
 * panels that are empty for a reason the user can't see, which is the bug this
 * screen exists to fix. The honest surface is a dead end with a way out.
 *
 * Contrast with the sibling `IdentityRequiredDialog`: that one IS a dialog,
 * correctly — the user has access and merely has to pick which identity to use.
 *
 * Two causes, both worth naming, because the fix differs:
 *   - the membership was removed while this tab stayed open
 *   - the URL outlived a sign-out and now belongs to a different account
 *
 * Disclosure rule: `tenantName` must be passed ONLY when the org already appears
 * in the caller's own tenant list. The server's 403 deliberately doesn't reveal
 * whether a tenant exists, and naming one we can't see would turn this screen
 * into the enumeration oracle the server refused to be.
 *
 * Rendered at App level (outside the workspace ThemedArea), so it applies the
 * theme class explicitly — same rule the sibling App dialogs follow.
 */
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

interface Props {
  /** Org display name — ONLY when it's in the user's own tenant list (see above). */
  tenantName: string | null
  /** The signed-in account, so the user can tell "wrong account" from "removed". */
  userEmail: string | null
  /** The refused tenant is a PERSONAL drive (#853) — not an organization, and "no
   *  longer a member" does not apply: say "this drive", and point a reader it was
   *  shared with at their own Drive. */
  personal?: boolean
  /** Leave for the personal Drive. */
  onGoToMyDrive: () => void
  /** Sign out — the fix when this is the wrong account. */
  onSignOut: () => void
}

export function TenantAccessDeniedScreen({
  tenantName,
  userEmail,
  personal = false,
  onGoToMyDrive,
  onSignOut,
}: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { t } = useTranslation('auth')

  const title = tenantName
    ? t('tenantAccessDenied.titleNamed', { tenantName })
    : personal
      ? t('tenantAccessDenied.titlePersonal')
      : t('tenantAccessDenied.titleUnnamed')

  return (
    <div className={`${themeClass} themed-bg-primary min-h-screen flex items-center justify-center p-6`}>
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 w-12 h-12 rounded-full themed-bg-tertiary flex items-center justify-center">
          <svg
            className="w-6 h-6 themed-icon"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
            />
          </svg>
        </div>

        <h1 className="text-lg font-semibold themed-text mb-2">{title}</h1>

        <p className="text-sm themed-text-secondary leading-relaxed">
          {personal ? t('tenantAccessDenied.bodyPersonal') : t('tenantAccessDenied.body')}
        </p>

        {userEmail && (
          <p className="mt-3 text-xs themed-text-muted">
            {t('tenantAccessDenied.signedInAs', { email: userEmail })}
          </p>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={onSignOut}
            className="themed-btn-secondary px-4 py-2 rounded-md text-sm font-medium"
          >
            {t('tenantAccessDenied.signInAsSomeoneElse')}
          </button>
          <button
            type="button"
            onClick={onGoToMyDrive}
            className="themed-btn-primary px-4 py-2 rounded-md text-sm font-medium"
          >
            {t('tenantAccessDenied.goToMyDrive')}
          </button>
        </div>
      </div>
    </div>
  )
}
